import React, { useState } from 'react';
import {
  makeStyles,
  Title2,
  Body1,
  tokens,
  Caption1,
  Button,
  Spinner,
  Input,
  Select,
} from '@fluentui/react-components';
import { 
  HeartRegular, 
  PersonRegular, 
  ChatRegular, 
  ClockRegular,
  SearchRegular,
  ArrowClockwiseRegular,
} from '@fluentui/react-icons';
import { PrayerSubmissionModal } from '../components/PrayerSubmissionModal';
import PrayerList from '../components/PrayerList';
import { getPrayers, PrayerItem, debugPrayerCommentsSchema, testPrayerCommentsIntegration } from '../services/sharePointService';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
    padding: '32px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: `linear-gradient(135deg, ${tokens.colorNeutralBackground1} 0%, ${tokens.colorNeutralBackground2} 100%)`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    marginBottom: '8px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    fontWeight: tokens.fontWeightRegular,
    lineHeight: '1.4',
    margin: 0,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', // Increased minimum width
    gap: '24px', // Increased gap
    marginBottom: '40px', // Increased margin
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', // Increased minimum width for mobile
      gap: '16px',
      marginBottom: '32px',
    },
  },
  statCard: {
    padding: '32px', // Increased padding
    borderRadius: '16px', // Increased border radius
    backgroundColor: tokens.colorNeutralBackground1,
    border: `2px solid ${tokens.colorNeutralStroke1}`, // Thicker border
    boxShadow: tokens.shadow4, // Enhanced shadow
    display: 'flex',
    alignItems: 'center',
    gap: '16px', // Increased gap
    transition: 'all 0.3s ease', // Smoother transition
    minHeight: '120px', // Minimum height for better visibility
    ':hover': {
      boxShadow: tokens.shadow8, // Enhanced hover shadow
      transform: 'translateY(-4px)', // More pronounced hover effect
      border: `2px solid ${tokens.colorBrandStroke1}`, // Brand color on hover
    },
  },
  statIcon: {
    width: '56px', // Increased size
    height: '56px', // Increased size
    borderRadius: '12px', // Increased border radius
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px', // Increased font size
    flexShrink: 0,
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: '8px', // Added gap between value and label
  },
  statValue: {
    fontSize: '32px', // Increased font size
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.1',
  },
  statLabel: {
    fontSize: '14px', // Increased font size
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
    textTransform: 'uppercase',
    letterSpacing: '0.8px', // Increased letter spacing
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  prayerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionDescription: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
  },
  filtersRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: '200px',
    flex: 1,
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  filterInput: {
    width: '100%',
  },
  filterSelect: {
    width: '100%',
  },
  filterActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    '@media (max-width: 768px)': {
      justifyContent: 'stretch',
    },
  },
  prayerListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
  },
  refreshButton: {
    marginLeft: 'auto',
  },
});

const PatientCare: React.FC = () => {
  const styles = useStyles();
  const [refreshKey, setRefreshKey] = useState(0);
  const [prayers, setPrayers] = useState<PrayerItem[]>([]);
  const [filteredPrayers, setFilteredPrayers] = useState<PrayerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter] = useState<string>('all');
  const [assignedFilter, setAssignedFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  const handlePrayerSubmitted = () => {
    // Trigger a refresh of the prayer list
    setRefreshKey(prev => prev + 1);
    loadPrayers();
  };

  const loadPrayers = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading prayers...');
      const prayersData = await getPrayers();
      console.log('📋 Prayers loaded:', prayersData);
      console.log('📋 Prayers length:', prayersData.length);
      console.log('📋 First prayer sample:', prayersData[0]);
      setPrayers(prayersData);
      setFilteredPrayers(prayersData);
    } catch (error) {
      console.error('❌ Error loading prayers:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadPrayers();
  }, []);

  React.useEffect(() => {
    // Apply filters and sorting
    let filtered = prayers;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(prayer =>
        prayer.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prayer.PrayerText.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prayer.CreatedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter (you can customize this based on your data structure)
    if (statusFilter !== 'all') {
      // Add status filtering logic here based on your data
    }

    // Assigned filter
    if (assignedFilter !== 'all') {
      if (assignedFilter === 'assigned') {
        filtered = filtered.filter(prayer => prayer.AssignedTo);
      } else if (assignedFilter === 'unassigned') {
        filtered = filtered.filter(prayer => !prayer.AssignedTo);
      }
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.Created).getTime();
      const dateB = new Date(b.Created).getTime();
      
      if (sortBy === 'newest') {
        return dateB - dateA;
      } else if (sortBy === 'oldest') {
        return dateA - dateB;
      } else if (sortBy === 'title') {
        return a.Title.localeCompare(b.Title);
      }
      return 0;
    });

    setFilteredPrayers(filtered);
  }, [prayers, searchTerm, statusFilter, assignedFilter, sortBy]);

  const getPrayerStats = () => {
    const total = prayers.length;
    const assigned = prayers.filter(p => p.AssignedTo).length;
    const unassigned = prayers.filter(p => !p.AssignedTo).length;
    const recent = prayers.filter(p => {
      const prayerDate = new Date(p.Created);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return prayerDate > weekAgo;
    }).length;

    return { total, assigned, unassigned, recent };
  };

  const stats = getPrayerStats();

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleContainer}>
            <Title2 className={styles.title}>Patient Care</Title2>
            <Body1 className={styles.subtitle}>
              Manage prayer requests and patient care activities with compassion and efficiency
            </Body1>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteBlueBackground2 }}>
            <HeartRegular />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total Prayers</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }}>
            <PersonRegular />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.assigned}</div>
            <div className={styles.statLabel}>Assigned</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteYellowBackground2 }}>
            <ClockRegular />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.unassigned}</div>
            <div className={styles.statLabel}>Unassigned</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPalettePurpleBackground2 }}>
            <ChatRegular />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.recent}</div>
            <div className={styles.statLabel}>This Week</div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Submit Prayer Section */}
        <div className={styles.prayerSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <HeartRegular />
              Submit a Prayer Request
            </h3>
            <p className={styles.sectionDescription}>
              Share prayer requests for patients, families, or care team members. Your requests will be handled with care and confidentiality.
            </p>
            <PrayerSubmissionModal onPrayerSubmitted={handlePrayerSubmitted} />
          </div>
        </div>

        {/* Prayer List Section */}
        <div className={styles.prayerSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <ChatRegular />
              Prayer Requests
            </h3>
            <p className={styles.sectionDescription}>
              View and manage all submitted prayer requests. Use the filters below to find specific requests.
            </p>
          </div>

          {/* Filters */}
          <div className={styles.filtersContainer}>
            <div className={styles.filtersRow}>
              <div className={styles.filterGroup}>
                <div className={styles.filterLabel}>Search</div>
                <Input
                  placeholder="Search prayers by title, content, or author..."
                  value={searchTerm}
                  onChange={(e, data) => setSearchTerm(data.value)}
                  className={styles.filterInput}
                  contentBefore={<SearchRegular />}
                />
              </div>
              <div className={styles.filterGroup}>
                <div className={styles.filterLabel}>Assignment Status</div>
                <Select
                  value={assignedFilter}
                  onChange={(e, data) => setAssignedFilter(data.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">All Prayers</option>
                  <option value="assigned">Assigned</option>
                  <option value="unassigned">Unassigned</option>
                </Select>
              </div>
              <div className={styles.filterGroup}>
                <div className={styles.filterLabel}>Sort By</div>
                <Select
                  value={sortBy}
                  onChange={(e, data) => setSortBy(data.value)}
                  className={styles.filterSelect}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                </Select>
              </div>
              <div className={styles.filterActions}>
                <Button
                  appearance="outline"
                  icon={<ArrowClockwiseRegular />}
                  onClick={loadPrayers}
                  disabled={loading}
                  className={styles.refreshButton}
                >
                  Refresh
                </Button>
                <Button
                  appearance="outline"
                  onClick={debugPrayerCommentsSchema}
                  className={styles.refreshButton}
                >
                  Debug Schema
                </Button>
                <Button
                  appearance="outline"
                  onClick={testPrayerCommentsIntegration}
                  className={styles.refreshButton}
                >
                  Test Comments
                </Button>
              </div>
            </div>
          </div>

          {/* Prayer List */}
          <div className={styles.prayerListContainer}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="large" label="Loading prayers..." />
              </div>
            ) : filteredPrayers.length === 0 ? (
              <div className={styles.emptyState}>
                <HeartRegular style={{ fontSize: '48px', marginBottom: '16px' }} />
                <Body1>No prayer requests found.</Body1>
                <Caption1>
                  {searchTerm || assignedFilter !== 'all' 
                    ? 'Try adjusting your filters or search terms.' 
                    : 'Prayer requests will appear here once submitted.'}
                </Caption1>
              </div>
            ) : (
              <PrayerList 
                key={refreshKey} 
                prayers={filteredPrayers} 
                onPrayerUpdated={loadPrayers} // Refresh prayers when comments are added
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientCare; 